import {
  USAGE_CATEGORIES,
  UsageCategory,
  UsageCategoryCounts,
  UsageGranularity,
} from "../../../api/admin/types";

// Categories tied to the Epic-runs toggle. Hidden / subtracted from displayed
// totals when showEpic is false. Currently just `epic_runs` but kept as a
// list so adding a future Epic sub-category is a one-line change.
export const EPIC_CATEGORIES: readonly UsageCategory[] = ["epic_runs"];

export function visibleCategories(showEpic: boolean): UsageCategory[] {
  if (showEpic) return Array.from(USAGE_CATEGORIES);
  const epic = new Set<UsageCategory>(EPIC_CATEGORIES);
  return USAGE_CATEGORIES.filter((c) => !epic.has(c));
}

// Subtract Epic categories from a workflow `total` returned by the BE when
// the user has toggled Epic visibility off. The BE always returns the full
// total (Epic included) — this helper makes the displayed total match the
// visible category cells.
export function adjustWorkflowTotal(
  total: number | undefined | null,
  totals: UsageCategoryCounts | undefined | null,
  showEpic: boolean
): number {
  const t = total ?? 0;
  if (showEpic || !totals) return t;
  let subtract = 0;
  for (const c of EPIC_CATEGORIES) {
    subtract += totals[c] ?? 0;
  }
  return t - subtract;
}

export type MetricsMode = "single" | "compare";
export type MetricsTab = "agency" | "user" | "role" | "policy";

export const GRANULARITY_OPTIONS: { value: UsageGranularity; label: string }[] = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "year", label: "Year" },
];

export const CATEGORY_COLORS: Record<UsageCategory, string> = {
  policies: "#7c4dff",
  comparisons: "#3949ab",
  single_policy_analyses: "#1e88e5",
  proposals: "#26a69a",
  contract_reviews: "#7cb342",
  gap_analyses: "#fdd835",
  sov: "#fb8c00",
  loss_runs: "#5e35b1",
  coi: "#e64a19",
  chats: "#6d4c41",
  epic_runs: "#0288d1",
};

export function toDateInputValue(d: Date): string {
  // YYYY-MM-DDTHH:mm  for <input type="datetime-local" />
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function fromDateInputValue(s: string): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

export function startOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

export function endOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(23, 59, 59, 999);
  return out;
}

export function defaultPeriodForMode(mode: MetricsMode): {
  startA: string;
  endA: string;
  startB: string;
  endB: string;
} {
  const now = new Date();
  const sundayOffset = (now.getDay() + 0) % 7; // Sunday = 0
  const startThisWeek = startOfDay(new Date(now));
  startThisWeek.setDate(startThisWeek.getDate() - sundayOffset);
  const endThisWeek = endOfDay(new Date(now));

  const startLastWeek = startOfDay(new Date(startThisWeek));
  startLastWeek.setDate(startLastWeek.getDate() - 7);
  const endLastWeek = endOfDay(new Date(startThisWeek));
  endLastWeek.setDate(endLastWeek.getDate() - 1);

  if (mode === "compare") {
    return {
      startA: toDateInputValue(startLastWeek),
      endA: toDateInputValue(endLastWeek),
      startB: toDateInputValue(startThisWeek),
      endB: toDateInputValue(endThisWeek),
    };
  }
  // For single mode: default to last 12 weeks
  const start = startOfDay(new Date(now));
  start.setDate(start.getDate() - 12 * 7);
  return {
    startA: toDateInputValue(start),
    endA: toDateInputValue(endThisWeek),
    startB: "",
    endB: "",
  };
}

export function isoFromDateInput(value: string): string {
  return new Date(value).toISOString();
}
